import React, { useEffect, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import ApiError from '~/types/ApiError';

import { isOfferTypeHotel } from '~/utils/offer';

import PackageSocieteRewardRow from './PackageSocieteRewardRow';
import PoolCard from './PoolCard';
import { withOfferIdParam } from './flashRewardsUrl';
import { parseOfferIdInput } from './parseOfferIdInput';

/**
 * Admin page for the Société flash-hotel reward program: find an offer, manage its reward pool
 * cap, and review/override the Société reward on each of its packages, all in one place.
 *
 * The offer-id field accepts a bare Salesforce offer id or a pasted admin URL (an offer detail
 * page, or a Flash Rewards deep link carrying `?offerId=`) — see `parseOfferIdInput`. Whatever id
 * is resolved mirrors into this page's own `?offerId=` query param so the page itself can be
 * deep-linked (e.g. from the offer detail page, or a link handed to support).
 */
export default function FlashRewardsOfferPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState(() => searchParams.get('offerId') ?? '');
  const offerId = parseOfferIdInput(inputValue);

  // Mirror the resolved id into the URL so the current lookup can be deep-linked. Only runs off
  // local input changes — it never reads the URL back into `inputValue` — so an in-progress,
  // not-yet-id-shaped keystroke is never clobbered by this effect clearing the query param.
  // Writes only when offerId actually differs from the URL (see withOfferIdParam): an unchanged
  // write on mount could restore a stale `?tab=` and bounce the Offers page to another sub-tab.
  // Sibling params (`tab`, `flashTab`) are kept.
  useEffect(() => {
    const next = withOfferIdParam(searchParams, offerId);
    if (next) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  // Shared offer hook: caches under offerQueryKeys.detail(id), so the offer detail page and this
  // page reuse one cache entry instead of each fetching its own copy.
  const offerQuery = useGetOffer(offerId ?? undefined);

  const offer = (offerQuery.data ?? null) as App.Offer | null;
  const notFound = offerQuery.error instanceof ApiError && offerQuery.error.status === 404;

  return (
    <Stack spacing={3} sx={{ py: 3 }}>
      <Typography variant="h5">Flash Hotel Rewards</Typography>

      <TextField
        label="Offer ID or URL"
        placeholder="Paste a Salesforce offer id or an admin offer URL"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        error={inputValue.trim().length > 0 && !offerId}
        helperText={
          inputValue.trim().length === 0
            ? 'Enter an offer Salesforce id or paste an admin offer URL'
            : !offerId
              ? "Couldn't find an offer id in that input"
              : ' '
        }
        fullWidth
      />

      {offerId && offerQuery.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {offerId && offerQuery.isError && notFound && <Alert severity="warning">Offer not found.</Alert>}

      {offerId && offerQuery.isError && !notFound && (
        <Alert severity="error">
          {offerQuery.error instanceof Error ? offerQuery.error.message : 'Failed to load the offer.'}
        </Alert>
      )}

      {offer && !isOfferTypeHotel(offer) && <Alert severity="warning">This is not a flash hotel offer.</Alert>}

      {offer && isOfferTypeHotel(offer) && (
        <>
          <PoolCard offerId={offer.id_salesforce_external} />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Package</TableCell>
                <TableCell>Société Reward</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {offer.packages.map((pkg) => (
                <TableRow key={pkg.unique_key}>
                  <TableCell>{pkg.name}</TableCell>
                  <TableCell>
                    <PackageSocieteRewardRow offerSfId={offer.id_salesforce_external} pkg={pkg} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Stack>
  );
}
